import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IndividualClientFileForm from "./IndividualClientFileForm";
import {
  fetchIndividualClientFile,
  updateIndividualClientFile,
  clearCurrentIndividualClientFile,
} from "../../store/slices/individualClientFileSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

// Normalise a date value to yyyy-mm-dd for <input type="date">
const toDate = (v) => (v ? String(v).substring(0, 10) : "");

const EditIndividualClientFile = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentIndividualClientFile, isLoading, error } = useSelector(
    (s) => s.individualClientFile
  );

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    goals: [],
    progress_notes: [],
    family_meetings: [],
    transition_activities: [],
  });

  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (id) dispatch(fetchIndividualClientFile(id));
    return () => dispatch(clearCurrentIndividualClientFile());
  }, [dispatch, id]);

  useEffect(() => {
    const rec = currentIndividualClientFile;
    if (rec && rec.id === parseInt(id)) {
      const {
        goals,
        // API serializes relations as snake_case keys
        progress_notes: progressNotes,
        family_meetings: familyMeetings,
        transition_activities: transitionActivities,
        patient, case: caseRel, creator, updater, deleter,
        created_at, updated_at, deleted_at,
        ...scalars
      } = rec;

      setFormData({
        ...scalars,
        // Normalise parent date fields
        date_of_birth: toDate(scalars.date_of_birth),
        date_of_enrollment: toDate(scalars.date_of_enrollment),
        program_start_date: toDate(scalars.program_start_date),
        stage1_date_achieved: toDate(scalars.stage1_date_achieved),
        stage2_date_achieved: toDate(scalars.stage2_date_achieved),
        stage3_date_achieved: toDate(scalars.stage3_date_achieved),
        stage4_date_achieved: toDate(scalars.stage4_date_achieved),
        planned_discharge_date: toDate(scalars.planned_discharge_date),
        reintegration_date: toDate(scalars.reintegration_date),
        actual_end_date: toDate(scalars.actual_end_date),
        client_sign_date: toDate(scalars.client_sign_date),
        family_rep_sign_date: toDate(scalars.family_rep_sign_date),
        psychiatrist_sign_date: toDate(scalars.psychiatrist_sign_date),
        prp_sign_date: toDate(scalars.prp_sign_date),
        case_manager_sign_date: toDate(scalars.case_manager_sign_date),
        leap_member_sign_date: toDate(scalars.leap_member_sign_date),
        goals: (goals || []).map((g) => ({ ...g, target_date: toDate(g.target_date) })),
        progress_notes: (progressNotes || []).map((p) => ({ ...p, note_date: toDate(p.note_date) })),
        family_meetings: (familyMeetings || []).map((f) => ({ ...f, meeting_date: toDate(f.meeting_date) })),
        transition_activities: (transitionActivities || []).map((t) => ({ ...t, initiated_date: toDate(t.initiated_date) })),
      });

      if (rec.patient_id) {
        dispatch(fetchPatient(rec.patient_id))
          .unwrap()
          .then((patient) => setPatientData(patient))
          .catch((err) => console.error("Failed to fetch patient details:", err));
      }
    }
  }, [currentIndividualClientFile, id, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateIndividualClientFile({ id, formData })).unwrap();
      Swal.fire({
        title: "Success!",
        text: "Individual Client File updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      let errorMessage = "Failed to update Individual Client File";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Client Name:</span>
                  <span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">MR Number:</span>
                  <span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Date of Birth:</span>
                  <span className="fw-bold text-dark">
                    {patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Gender:</span>
                  <span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Edit Individual Client File</h5>
            <small className="text-muted">Comprehensive Psychiatric Rehabilitation Framework (CARF-Aligned)</small>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4">{error}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <IndividualClientFileForm formData={formData} setFormData={setFormData} />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100">
                      <i className="ri-save-3-line me-1"></i> Update
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditIndividualClientFile;
